import type { CveEntry } from "./normalize.js";

/**
 * NVD API 2.0 client.
 *
 * Why this can exist with no backend: NVD sends
 * `access-control-allow-origin: *`, so a static page may fetch it directly.
 * Verified live during planning; re-verified by tests using recorded shapes,
 * never by hitting the live API in CI.
 *
 * Why no API key: a key in static JavaScript is public, scraped, and revoked.
 * The anonymous limit is 5 requests per rolling 30 seconds.
 */

export const NVD_ENDPOINT = "https://services.nvd.nist.gov/rest/json/cves/2.0";

/** Anonymous NVD allowance. */
export const MAX_REQUESTS_PER_WINDOW = 5;
export const RATE_WINDOW_MS = 30_000;
/**
 * Positive margin. A request exactly 30 s old still sits inside an inclusive
 * [t-30s, t] window; without the margin a sixth request slips through.
 */
export const RATE_SAFETY_MS = 250;

/**
 * Page size for one NVD request.
 *
 * 2000 is NVD's own maximum, but a 7-day window at that size is ~4.9 MB of JSON
 * that NVD trickles rather than bursts. Measured on a normal connection:
 *
 *   resultsPerPage=500   ->  1.6 MB,  33 s, complete
 *   resultsPerPage=1000  ->  3.2 MB,  82 s, complete
 *   resultsPerPage=2000  ->  4.9 MB, 150 s+, transfer never completed
 *
 * The truncated body is invalid JSON, so the fetch fails. Smaller pages complete
 * individually, but page size is not the lever: ten 500-record pages for a
 * whole week still overran the budget. The total bytes per fetch are, which is
 * why the pool fetches one UTC day at a time and persists every page.
 */
export const DEFAULT_PAGE_SIZE = 500;

/**
 * Pages fetched per call, at most.
 *
 * The pool fetches one UTC day per call, and a day is ~670 records (4,695 in a
 * measured 7-day window), so two pages at the size above. The cap only bounds a
 * pathological day. A capped call reports `partial`, and the caller persists
 * `nextStartIndex` so the next refresh resumes where this one stopped.
 */
export const MAX_PAGES_PER_FETCH = 6;

/**
 * Total budget for one call, which is one day of the pool.
 *
 * Sized against the measurement above: a day is two 500-record pages, roughly
 * 70 s of transfer. The station still starts before the network answers — music
 * comes from the generative bed, and a slow or failed fetch simply means
 * narration uses the cached pool or the seeds — so this budget governs the
 * *pool*, not whether the page makes sound. Pages that arrived before the
 * budget ran out are already persisted through `onPage`.
 */
export const DEFAULT_TOTAL_BUDGET_MS = 240_000;
export const MAX_ATTEMPTS = 4;

export interface NvdDescription {
  lang: string;
  value: string;
}

export interface NvdMetricData {
  /** NVD nests the score inside `cvssData` for v3.x/v4.0; v2 keeps severity outside. */
  cvssData?: { baseScore?: number; baseSeverity?: string };
  baseScore?: number;
  baseSeverity?: string;
}

export interface NvdCve {
  id: string;
  published?: string;
  lastModified?: string;
  vulnStatus?: string;
  descriptions?: NvdDescription[];
  metrics?: Record<string, NvdMetricData[] | undefined>;
}

export interface NvdVulnerability {
  cve: NvdCve;
}

export interface NvdResponse {
  resultsPerPage: number;
  startIndex: number;
  totalResults: number;
  vulnerabilities: NvdVulnerability[];
}

export interface FetchPoolOptions {
  windowStartUtc: string;
  windowEndUtc: string;
  signal: AbortSignal;
  pageSize?: number;
  /** NVD `startIndex` to resume from. Defaults to 0. */
  startIndex?: number;
  /** Cap on pages per fetch. Defaults to `MAX_PAGES_PER_FETCH`. */
  maxPages?: number;
  /** Injectable clock, so limit tests can use a fake one. */
  now?: () => number;
  /** Injectable sleep, so limit tests do not actually wait. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  /** Injectable fetch. */
  fetchImpl?: typeof fetch;
  totalBudgetMs?: number;
  maxAttempts?: number;
  onProgress?: (loaded: number, total: number) => void;
  /**
   * Called after each page is parsed, before the next is requested. Awaited, so
   * the caller can persist the page: a later page that fails must not take the
   * earlier ones down with it.
   */
  onPage?: (page: FetchedPage) => void | Promise<void>;
  /**
   * Shared rolling limiter. NVD's anonymous quota is per IP, not per call, so
   * every dispatch in the page must go through one limiter. Without this, a
   * stale-cache background refresh and a replacement station's fetch could each
   * dispatch five requests inside the same 30 s window.
   */
  limiter?: RollingLimiter;
}

export interface FetchedPage {
  /** Entries from this page only. */
  entries: CveEntry[];
  nextStartIndex: number;
  totalResults: number;
  /** The window is exhausted; there is no next page. */
  done: boolean;
}

export interface FetchPoolResult {
  entries: CveEntry[];
  totalResults: number;
  requests: number;
  /** Where the next fetch of this window should resume. */
  nextStartIndex: number;
  /** True when the page cap stopped the fetch before the window was exhausted. */
  partial: boolean;
}

export class NvdError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = "NvdError";
  }
}

/**
 * Rolling-window limiter. A deque of every dispatch timestamp, retries
 * included. A token bucket is wrong here: it can admit a sixth request inside a
 * rolling window after refill.
 */
export class RollingLimiter {
  private readonly stamps: number[] = [];

  constructor(
    private readonly now: () => number = Date.now,
    private readonly limit: number = MAX_REQUESTS_PER_WINDOW,
    private readonly windowMs: number = RATE_WINDOW_MS,
    private readonly safetyMs: number = RATE_SAFETY_MS,
  ) {}

  /** Milliseconds the caller must wait before the next dispatch is legal. */
  delayUntilNext(): number {
    this.prune(this.now());
    if (this.stamps.length < this.limit) return 0;
    const oldest = this.stamps[0] as number;
    return Math.max(0, oldest + this.windowMs + this.safetyMs - this.now());
  }

  /** Record a dispatch. Call this for retries too. */
  record(): void {
    this.stamps.push(this.now());
  }

  /** Current occupancy inside the rolling window; used by assertions. */
  occupancy(at: number = this.now()): number {
    this.prune(at);
    return this.stamps.length;
  }

  private prune(at: number): void {
    while (this.stamps.length > 0 && (this.stamps[0] as number) < at - this.windowMs) {
      this.stamps.shift();
    }
  }
}

export function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function parseRetryAfter(header: string | null, nowMs: number): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(header);
  if (Number.isFinite(date)) return Math.max(0, date - nowMs);
  return undefined;
}

function backoffMs(attempt: number, rng: () => number = Math.random): number {
  const base = Math.min(2 ** attempt * 750, 15_000);
  return Math.round(base * (0.5 + rng() * 0.5));
}

function pickScore(metrics: NvdCve["metrics"]): { score?: number; severity?: string } {
  if (!metrics) return {};
  const datas: NvdMetricData[] = [
    ...(metrics.cvssMetricV40 ?? []),
    ...(metrics.cvssMetricV31 ?? []),
    ...(metrics.cvssMetricV30 ?? []),
    ...(metrics.cvssMetricV2 ?? []),
  ];
  const first = datas[0];
  if (!first) return {};
  return {
    score: first.cvssData?.baseScore ?? first.baseScore,
    severity: first.cvssData?.baseSeverity ?? first.baseSeverity,
  };
}

/** Filtering is not cosmetic: rejected records have no description worth airing. */
export function toEntry(vulnerability: NvdVulnerability): CveEntry | null {
  const cve = vulnerability?.cve;
  if (!cve?.id) return null;
  if (cve.vulnStatus === "Rejected") return null;
  const english = cve.descriptions?.find((item) => item.lang === "en");
  const description = english?.value?.trim();
  if (!description) return null;
  const { score, severity } = pickScore(cve.metrics);
  return {
    id: cve.id,
    description,
    published: cve.published,
    lastModified: cve.lastModified,
    baseScore: score,
    severity,
  };
}

export function parseResponse(payload: unknown): NvdResponse {
  const record = (payload ?? {}) as Partial<NvdResponse>;
  return {
    resultsPerPage: Number(record.resultsPerPage ?? 0),
    startIndex: Number(record.startIndex ?? 0),
    totalResults: Number(record.totalResults ?? 0),
    vulnerabilities: Array.isArray(record.vulnerabilities) ? record.vulnerabilities : [],
  };
}

function buildUrl(
  windowStartUtc: string,
  windowEndUtc: string,
  startIndex: number,
  pageSize: number,
  endpoint: string,
): string {
  // NVD wants ISO-8601 with an explicit offset. UTC midnight.
  const params = new URLSearchParams({
    pubStartDate: `${windowStartUtc}T00:00:00.000Z`,
    pubEndDate: `${windowEndUtc}T00:00:00.000Z`,
    resultsPerPage: String(pageSize),
    startIndex: String(startIndex),
  });
  return `${endpoint}?${params.toString()}`;
}

/**
 * Read and parse a JSON body, but give up after `ms`.
 *
 * `Response.json()` has no timeout of its own, so a server that sends headers
 * promptly and then stalls mid-body — which is what NVD does under load — holds
 * the caller indefinitely. The `signal` is the request's own, so aborting it
 * releases the socket rather than merely abandoning the promise.
 */
async function readJsonWithin(
  response: Response,
  ms: number,
  signal: AbortSignal,
): Promise<NvdResponse> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new NvdError("timed out reading the NVD response body")), ms);
  });

  try {
    const text = await Promise.race([response.text(), timeout]);
    return JSON.parse(text) as NvdResponse;
  } catch (error) {
    if (error instanceof NvdError) throw error;
    if (signal.aborted) throw new NvdError("NVD request aborted");
    throw new NvdError(`unreadable NVD response: ${(error as Error).message}`);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** `AbortSignal.any` where available, with a manual fallback for older engines. */
function combineSignals(signals: AbortSignal[]): AbortSignal {
  const anyOf = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyOf === "function") return anyOf(signals);

  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return controller.signal;
}

async function requestJson(
  url: string,
  options: FetchPoolOptions,
  limiter: RollingLimiter,
  deadlineMs: number,
): Promise<NvdResponse> {
  const now = options.now ?? (() => Date.now());
  const sleep = options.sleep ?? defaultSleep;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const maxAttempts = options.maxAttempts ?? MAX_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const wait = limiter.delayUntilNext();
    if (wait > 0) await sleep(wait, options.signal);
    limiter.record();

    let response: Response;
    // Bound the whole request — connection, headers, and body — by the caller's
    // budget, so a stalled server cannot hold this call open indefinitely.
    const requestBudgetMs = Math.max(1, deadlineMs - now());
    const requestSignal = combineSignals([
      options.signal,
      AbortSignal.timeout(requestBudgetMs),
    ]);
    try {
      response = await fetchImpl(url, {
        signal: requestSignal,
        // The wildcard ACAO is incompatible with credentialed CORS.
        credentials: "omit",
        headers: { accept: "application/json" },
      });
    } catch (error) {
      if (options.signal.aborted) throw error;
      if (attempt === maxAttempts - 1) {
        throw new NvdError(`network failure: ${(error as Error).message}`);
      }
      await sleep(backoffMs(attempt), options.signal);
      continue;
    }

    if (response.ok) {
      // The budget must cover reading the body, not just receiving headers.
      // NVD answers `200` immediately and then trickles the payload: a
      // multi-megabyte page can take minutes, and a budget checked only between
      // requests can never fire. Combined with a caller that awaits the first
      // slot's preparation, that is an unbounded wait before any audio.
      const remaining = deadlineMs - now();
      if (remaining <= 0) throw new NvdError("fetch budget exceeded while reading a page");
      return parseResponse(await readJsonWithin(response, remaining, requestSignal));
    }

    const retryAfter = parseRetryAfter(response.headers.get("retry-after"), now());
    const retryable = response.status === 403 || response.status === 429 || response.status >= 500;
    if (!retryable || attempt === maxAttempts - 1) {
      throw new NvdError(
        `NVD responded ${response.status}`,
        response.status,
        retryAfter,
      );
    }
    await sleep(retryAfter ?? backoffMs(attempt), options.signal);
  }

  throw new NvdError("exhausted attempts");
}

/**
 * Fetch every page of a window. Terminates exactly when
 * `startIndex + resultsPerPage >= totalResults`, mirroring NVD's own contract.
 */
export async function fetchPool(options: FetchPoolOptions): Promise<FetchPoolResult> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const maxPages = options.maxPages ?? MAX_PAGES_PER_FETCH;
  const now = options.now ?? (() => Date.now());
  const deadline = now() + (options.totalBudgetMs ?? DEFAULT_TOTAL_BUDGET_MS);
  const limiter = options.limiter ?? new RollingLimiter(now);
  const entries: CveEntry[] = [];
  let startIndex = options.startIndex ?? 0;
  let totalResults = 0;
  let requests = 0;

  for (;;) {
    if (now() > deadline) throw new NvdError("fetch budget exceeded");
    const url = buildUrl(options.windowStartUtc, options.windowEndUtc, startIndex, pageSize, NVD_ENDPOINT);
    const page = await requestJson(url, options, limiter, deadline);
    requests += 1;
    totalResults = page.totalResults;
    const pageEntries: CveEntry[] = [];
    for (const vulnerability of page.vulnerabilities) {
      const entry = toEntry(vulnerability);
      if (entry) pageEntries.push(entry);
    }
    entries.push(...pageEntries);
    options.onProgress?.(Math.min(startIndex + page.vulnerabilities.length, totalResults), totalResults);

    startIndex += page.resultsPerPage || pageSize;
    const done = startIndex >= totalResults || page.vulnerabilities.length === 0;
    await options.onPage?.({ entries: pageEntries, nextStartIndex: startIndex, totalResults, done });
    if (done) break;
    // Bounded work per call. The loop is otherwise "keep going until the window
    // is exhausted", which turns a busy window into an unbounded number of slow
    // multi-megabyte requests. `partial` plus `nextStartIndex` let the caller
    // resume later instead of mistaking a prefix for the whole window.
    if (requests >= maxPages) {
      return { entries, totalResults, requests, nextStartIndex: startIndex, partial: true };
    }
  }

  return { entries, totalResults, requests, nextStartIndex: startIndex, partial: false };
}
